import { useState } from 'react';
import { AlertTriangle, CheckCircle2, FileClock, RefreshCw } from 'lucide-react';
import type { ProjectRdShare } from '@edison/api-client-react';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Bar } from '@/components/ui/readiness-row.js';

/** A core/supporting/out percentage triple. */
export interface Triple {
  corePct: number;
  supportingPct: number;
  outPct: number;
}

/** Payload a row hands up to the tab when the reviewer clicks Adopt — the tab owns the
 * confirmation dialog + mutation so only one dialog instance exists for the whole tab. */
export interface AdoptRequest {
  projectId: string;
  projectName: string;
  incomeYear: string;
  pending: Triple;
  derived: Triple | null;
}

/** One project's row in the shares tab — joins a (possibly absent) `ProjectRdShare` with the
 * project attributes the row needs to render (name, TFA lock, non-R&D status). */
export interface ShareRowData {
  projectId: string;
  projectName: string;
  tfaLocked: boolean | null;
  isNoRd: boolean;
  share?: ProjectRdShare;
}

function pctOrZero(v: string | null | undefined): number {
  return v != null ? parseFloat(v) : 0;
}

function fmtPct(n: number): string {
  return `${Math.round(n * 10) / 10}%`;
}

interface Props {
  row: ShareRowData;
  canManage: boolean;
  onOpenDrilldown: (projectId: string, projectName: string) => void;
  onRequestAdopt: (request: AdoptRequest) => void;
}

/**
 * One project's R&D share row: the AI-derived triple (read-only, per bucket_classification
 * counts) alongside an editable "adopted" triple a reviewer can accept as-is or override, mirroring
 * the AI-proposes/human-confirms interaction of timewriting's BucketCard + MoveBucketModal.
 * The Adopt button only opens the confirmation dialog (owned by the parent tab) once the three
 * inputs sum to exactly 100 — Rule Zero: a share that doesn't sum to 100 is never submittable.
 */
export function ShareRow({ row, canManage, onOpenDrilldown, onRequestAdopt }: Props) {
  const share = row.share;

  const derivedCore = pctOrZero(share?.derivedCorePct);
  const derivedSupporting = pctOrZero(share?.derivedSupportingPct);
  const derivedOut = pctOrZero(share?.derivedOutPct);

  const initialCore = share?.adoptedCorePct != null ? pctOrZero(share.adoptedCorePct) : derivedCore;
  const initialSupporting = share?.adoptedSupportingPct != null ? pctOrZero(share.adoptedSupportingPct) : derivedSupporting;
  const initialOut = share?.adoptedOutPct != null ? pctOrZero(share.adoptedOutPct) : derivedOut;

  const [core, setCore] = useState(initialCore);
  const [supporting, setSupporting] = useState(initialSupporting);
  const [out, setOut] = useState(initialOut);

  const sum = core + supporting + out;
  const sumValid = Math.abs(sum - 100) < 0.01;
  const commitCount = share?.derivedCommitCount ?? null;

  function handleAdoptClick() {
    if (!share) return;
    const derived = share.derivedCorePct != null && share.derivedSupportingPct != null && share.derivedOutPct != null
      ? { corePct: derivedCore, supportingPct: derivedSupporting, outPct: derivedOut }
      : null;
    onRequestAdopt({
      projectId: row.projectId,
      projectName: row.projectName,
      incomeYear: share.incomeYear,
      pending: { corePct: core, supportingPct: supporting, outPct: out },
      derived,
    });
  }

  return (
    <div className="rounded-lg border p-4 space-y-3" data-testid={`share-row-${row.projectId}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-sm truncate">{row.projectName}</p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            {row.isNoRd && <Badge variant="default">Non-R&amp;D</Badge>}
            {!row.isNoRd && row.tfaLocked !== true && (
              <Badge variant="warning">
                <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                TFA Required
              </Badge>
            )}
            {share?.status === 'stale' && (
              <Badge variant="warning">
                <RefreshCw className="h-3 w-3" aria-hidden="true" />
                Stale — re-derived split changed
              </Badge>
            )}
            {share?.status === 'adopted' && (
              <Badge variant="positive">
                <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
                Adopted
              </Badge>
            )}
            {share?.status === 'derived' && (
              <Badge variant="info">
                <FileClock className="h-3 w-3" aria-hidden="true" />
                Derived — awaiting adoption
              </Badge>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <span className="font-mono text-xs tabular-nums text-muted-foreground">
            {commitCount != null ? `${commitCount} commit${commitCount === 1 ? '' : 's'}` : '—'}
          </span>
          <Button variant="secondary" size="sm" onClick={() => onOpenDrilldown(row.projectId, row.projectName)}>
            View commits
          </Button>
        </div>
      </div>

      {row.isNoRd && (
        <p className="text-xs text-muted-foreground">
          Marked non-R&amp;D for this income year — 100% out, no adoption needed.
        </p>
      )}

      {!row.isNoRd && !share && (
        <p className="text-xs text-muted-foreground">Not yet derived for this income year — run derivation above.</p>
      )}

      {!row.isNoRd && share && (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <Bar label="Core (AI-derived)" value={fmtPct(derivedCore)} pct={derivedCore} tone="indigo" />
            <Bar label="Supporting (AI-derived)" value={fmtPct(derivedSupporting)} pct={derivedSupporting} tone="indigo" />
            <Bar label="Out (AI-derived)" value={fmtPct(derivedOut)} pct={derivedOut} tone="indigo" />
          </div>

          <div className="grid grid-cols-1 gap-3 border-t pt-3 md:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs text-muted-foreground" htmlFor={`${row.projectId}-core`}>Core (adopt)</label>
              <input
                id={`${row.projectId}-core`}
                type="number"
                min={0}
                max={100}
                step={0.1}
                disabled={!canManage}
                value={core}
                onChange={(e) => setCore(e.target.value === '' ? 0 : parseFloat(e.target.value))}
                className="w-full rounded-lg border bg-background px-2 py-1 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground" htmlFor={`${row.projectId}-supporting`}>Supporting (adopt)</label>
              <input
                id={`${row.projectId}-supporting`}
                type="number"
                min={0}
                max={100}
                step={0.1}
                disabled={!canManage}
                value={supporting}
                onChange={(e) => setSupporting(e.target.value === '' ? 0 : parseFloat(e.target.value))}
                className="w-full rounded-lg border bg-background px-2 py-1 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground" htmlFor={`${row.projectId}-out`}>Out (adopt)</label>
              <input
                id={`${row.projectId}-out`}
                type="number"
                min={0}
                max={100}
                step={0.1}
                disabled={!canManage}
                value={out}
                onChange={(e) => setOut(e.target.value === '' ? 0 : parseFloat(e.target.value))}
                className="w-full rounded-lg border bg-background px-2 py-1 text-sm"
              />
            </div>
          </div>

          <div className="flex items-center justify-between gap-3">
            <p className={`font-mono text-xs tabular-nums ${sumValid ? 'text-muted-foreground' : 'text-destructive'}`}>
              Sum: {Math.round(sum * 10) / 10}% {sumValid ? '' : '— must equal 100%'}
            </p>
            {canManage && (
              <Button variant="primary" size="sm" disabled={!sumValid} onClick={handleAdoptClick}>
                Adopt
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
